class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.viewport = this.querySelector('[data-gallery-viewport]');
    this.slides = Array.from(this.querySelectorAll('.product-gallery__slide'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.thumbsEl = this.querySelector('[data-gallery-thumbs]');
    this.counter = this.querySelector('[data-gallery-counter]');
    this.upBtn = this.querySelector('[data-thumbs-up]');
    this.downBtn = this.querySelector('[data-thumbs-down]');
    this.activeIndex = 0;

    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.go(this.activeIndex - 1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.go(this.activeIndex + 1));
    this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.go(Number(thumb.dataset.index))));

    this.querySelectorAll('[data-zoom-trigger]').forEach((btn) => {
      btn.addEventListener('click', () => this.openZoom(Number(btn.dataset.index)));
    });

    this.upBtn?.addEventListener('click', () => this.scrollThumbs(-1));
    this.downBtn?.addEventListener('click', () => this.scrollThumbs(1));
    this.thumbsEl?.addEventListener('scroll', () => this.updateRailButtons(), { passive: true });
    window.addEventListener('resize', () => this.updateRailButtons());
    this.updateRailButtons();

    this.setupSwipe();
  }

  // Scroll the thumbnail strip by roughly one screenful (vertical on desktop).
  scrollThumbs(direction) {
    const el = this.thumbsEl;
    if (!el) return;
    el.scrollBy({ top: direction * el.clientHeight * 0.8, behavior: 'smooth' });
  }

  updateRailButtons() {
    const el = this.thumbsEl;
    if (!el || !this.upBtn || !this.downBtn) return;
    this.upBtn.disabled = el.scrollTop <= 1;
    this.downBtn.disabled = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
  }

  // Keep the active thumbnail centred in whichever direction the strip scrolls.
  revealThumb(thumb) {
    const el = this.thumbsEl;
    if (!el || !thumb) return;
    const c = el.getBoundingClientRect();
    const t = thumb.getBoundingClientRect();
    const vertical = el.scrollHeight > el.clientHeight + 1;
    if (vertical) {
      el.scrollBy({ top: t.top - c.top - (c.height - t.height) / 2, behavior: 'smooth' });
    } else {
      el.scrollBy({ left: t.left - c.left - (c.width - t.width) / 2, behavior: 'smooth' });
    }
  }

  go(index) {
    if (index < 0) index = this.slides.length - 1;
    if (index >= this.slides.length) index = 0;
    const previous = this.activeIndex;
    this.slides[this.activeIndex].hidden = true;
    this.thumbs[this.activeIndex]?.classList.remove('is-active');
    this.thumbs[this.activeIndex]?.setAttribute('aria-selected', 'false');
    this.activeIndex = index;
    const slide = this.slides[this.activeIndex];
    slide.hidden = false;
    if (index !== previous && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // Slide + fade the new image in from the side it logically comes from.
      const last = this.slides.length - 1;
      let direction = index > previous ? 1 : -1;
      if (previous === last && index === 0) direction = 1;
      if (previous === 0 && index === last) direction = -1;
      slide.style.setProperty('--gallery-dir', direction);
      slide.classList.remove('is-entering');
      void slide.offsetWidth;
      slide.classList.add('is-entering');
      slide.addEventListener('animationend', () => slide.classList.remove('is-entering'), { once: true });
    }
    this.thumbs[this.activeIndex]?.classList.add('is-active');
    this.thumbs[this.activeIndex]?.setAttribute('aria-selected', 'true');
    this.revealThumb(this.thumbs[this.activeIndex]);
    if (this.counter) this.counter.textContent = `${this.activeIndex + 1} / ${this.slides.length}`;
  }

  goToMediaId(mediaId) {
    const index = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
    if (index > -1) this.go(index);
  }

  setupSwipe() {
    let startX = 0;
    this.viewport?.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
    this.viewport?.addEventListener(
      'touchend',
      (e) => {
        const deltaX = e.changedTouches[0].clientX - startX;
        if (Math.abs(deltaX) < 40) return;
        this.go(deltaX < 0 ? this.activeIndex + 1 : this.activeIndex - 1);
      },
      { passive: true }
    );
  }

  openZoom(index) {
    const img = this.slides[index]?.querySelector('img');
    if (!img) return;
    const overlay = document.createElement('div');
    overlay.className = 'product-gallery__lightbox';
    overlay.innerHTML = `<img src="${img.currentSrc || img.src}" alt="${img.alt}"><button type="button" class="product-gallery__lightbox-close" aria-label="Close">&times;</button>`;
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
    const close = () => {
      overlay.remove();
      document.body.style.overflow = '';
    };
    overlay.addEventListener('click', close);
    document.addEventListener('keydown', function onKey(e) {
      if (e.key === 'Escape') {
        close();
        document.removeEventListener('keydown', onKey);
      }
    });
  }
}

customElements.define('product-gallery', ProductGallery);
